import { PositionType } from '../../../lib/defs/types';
import {
  despawnOverlayEntity,
  spawnOverlayEntity,
} from '../../entities/OverlayEntity';
import { GameScene } from '../../GameScene';
import { IEffect } from './IEffect';
import { waitAFrame } from '../../../lib/util/waitAFrame';
import { SmokeAnimation } from '../../animations/SmokeAnimation';
import { CandyBlockEntity, spawnBlockEntity } from '../../entities/BlockEntity';
import { Vector2 } from '../../../odie';
import { gsap } from 'gsap';
import { config } from '../../defs/config';

// types
//-----------------------------------------------------------------------------
export type CandyBagEffectOptions = {
  position: PositionType;
};

/*
    candy bag effect
*/
export class CandyBagEffect implements IEffect {
  // fields
  //-------------------------------------------------------------------------
  // input
  private readonly _scene: GameScene;
  private readonly _position: PositionType;

  // init
  //-------------------------------------------------------------------------
  constructor(scene: GameScene, options: CandyBagEffectOptions) {
    this._scene = scene;
    this._position = options.position;
  }

  public static assets(): string[] {
    return [];
  }

  // impl
  //-------------------------------------------------------------------------
  public async execute() {
    // wait a frame since we async the animation
    await waitAFrame();

    // animate
    this._animate();
  }

  // private: animation
  //-------------------------------------------------------------------------
  private async _animate() {
    const size = { width: 2, height: 2 };

    // create animation
    const animation = new SmokeAnimation({
      size,
      tint: 0xffde1f,
    });

    // spawn animation as overlay
    const entity = spawnOverlayEntity(
      this._scene,
      this._position,
      size,
      () => animation,
    );

    const delay = 0.035;

    // run animations
    await Promise.all([
      animation.start(),
      this._animateCandySpawn(),
      this._animateCandySpawn(1, 0, delay),
      this._animateCandySpawn(0, 1, delay * 2),
      this._animateCandySpawn(1, 1, delay * 3),
    ]);

    // despawn entity
    despawnOverlayEntity(this._scene, entity);
  }

  private async _animateCandySpawn(offsetX = 0, offsetY = 0, delay = 0) {
    const position = new Vector2(
      this._position.x + offsetX,
      this._position.y + offsetY,
    );

    // spawn candy
    const candy = spawnBlockEntity(
      this._scene,
      { id: 'candy' },
      position,
    ) as CandyBlockEntity;
    const sprite = candy.c.blockCandy.view.sprite;
    const targetX = sprite.x;
    const targetY = sprite.y;
    const targetSX = sprite.scaleX;
    const targetSY = sprite.scaleY;
    const scaleValue = 1.5;
    const size = config.tile.size * 0.5;
    const startX = offsetX > 0 ? -size : size;
    const startY = offsetY > 0 ? -size : size;
    const duration = 0.8;

    sprite.x = startX;
    sprite.y = startY;
    sprite.scaleX = targetX * scaleValue;
    sprite.scaleY = targetY * scaleValue;
    sprite.alpha = 0;
    sprite.rotation = Math.PI * 2;

    // animate candy
    await gsap
      .timeline({ delay })
      .to(sprite, { duration, alpha: 1, ease: 'quad.out' })
      .to(
        sprite,
        {
          duration,
          scaleX: targetSX,
          scaleY: targetSY,
          ease: 'bounce.out',
        },
        0,
      )
      .to(
        sprite,
        {
          duration,
          x: targetX,
          y: targetY,
          ease: 'quad.inOut',
        },
        0,
      )
      .to(
        sprite,
        {
          duration,
          rotation: 0,
          ease: 'back.out',
        },
        0,
      );
  }
}
